import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useMachine } from '@xstate/react'
import { useSelector } from 'react-redux'
import signupMachine, { initialContext } from './accountFlowMachine'
import {
  selectCoupon,
  selectPricingConfig,
  selectRegularPricingConfig,
} from '../../store/pricingSlice'
import { getItem, getSessionItem, setSessionItem } from '../../storeManager'
import SignUpEmailPassword from '../SignUp/SignUpEmailPassword'
import SignUpChoosePlan from '../SignUp/SignUpChoosePlanStep/SignUpChoosePlan'
import SignUpFreePurchase from '../SignUp/SignUpFreePurchase'
import SignUpGoal from '../SignUp/SignUpGoal/SignUpGoal'
import SignUpPickAccountMethod from '../SignUp/SignUpPickAccountMethod'
import SignUpStats from '../SignUp/SignUpStats'
import SignUpSetUp from '../SignUp/SignUpSetUp'
import { selectIsLoggedIn, selectLimitedProfile } from '../../store/userSlice'
import Login from '../Login/Login'
import SignUpOrLogin from '../SignUp/SignUpOrLogin'
import goals from 'js/goals'
import ProgressIndicator from './ProgressIndicator'
import SignUpPayment from '../SignUp/SignUpPayment'
import { useRef } from 'react'
import ManageCoupon from './ManageCoupon'
import {
  setEntryPoint,
  trackSignUpFunnelStep,
  trackAccountFlowStep,
} from '../../analytics/tracking'
import { useNavigate } from 'react-router-dom'
import useLocale from 'js/hooks/useLocale'
import { pushToDataLayer } from 'js/pages/LandingPages/utils'

const MachineContext = React.createContext({})

function Switch({ children, current }) {
  return React.Children.map(children, (child) => {
    if (current.matches(child.props.state)) return child
    return null
  })
}
Switch.propTypes = {
  children: PropTypes.node,
  current: PropTypes.object,
}

function Match({ children }) {
  if (children) return children
  return null
}
Match.propTypes = {
  children: PropTypes.node,
}

function AccountFlow({
  defaultGoal = null,
  forcedGoal = false,
  discount = 0,
  onlySignup = false,
  onSignUpClick,
  onLoginClick,
  scenario = 'signup',
  initialPlan,
  pricingConfig,
  hideCoupon,
  initialUserData = {},
}) {
  const navigate = useNavigate()
  const containerRef = useRef()
  const sessionKey = 'accountflow'
  const { country } = useLocale()

  const startContext = {
    userData: JSON.parse(getSessionItem(sessionKey)) || {
      ...initialContext.userData,
      country,
    },
    discount,
    isLoggedIn: useSelector(selectIsLoggedIn),
    limitedProfile: useSelector(selectLimitedProfile),
    onlySignup,
    scenario,
    plan: initialPlan || JSON.parse(getSessionItem('initialPlan')),
    forcedGoal,
  }

  if (defaultGoal) startContext.userData.goal = defaultGoal

  startContext.userData = { ...startContext.userData, ...initialUserData }

  const [current, send, service] = useMachine(signupMachine, {
    devTools: true,
    context: startContext,
  })

  service.onChange((context) => {
    const { password: _, ...userData } = context.userData
    setSessionItem(sessionKey, JSON.stringify(userData))
    setSessionItem('initialPlan', JSON.stringify(context.plan))
  })

  let hasBackButton = false

  if (current.nextEvents.includes('PREV')) {
    // Check if the guards for PREV allows the transition to run
    hasBackButton = service.machine.transition(current, {
      type: 'PREV',
    }).changed
  }

  const scroll = () => {
    const scrollingElement =
      document.scrollingElement || document.documentElement
    const destination =
      containerRef.current.getBoundingClientRect().top +
      scrollingElement.scrollTop -
      75
    scrollingElement.scrollTop =
      containerRef.current && destination > 0 ? destination : 1
  }
  const [lastStateValue, setLastStateValue] = useState()

  const stepsToTrack = [
    'choosePlan',
    'decisionScreen',
    'logIn',
    'signUp.goal',
    'signUp.stats',
    'signUp.pickAccountMethod',
    'signUp.emailAccountCreation',
    'signUp.setUp',
    'subscription.freePurchase',
    'subscription.choosePlan',
    'subscription.pay',
  ]
  useEffect(() => {
    if (pricingConfig.discount === 100) return null
    if (JSON.stringify(current.value) !== lastStateValue) {
      stepsToTrack.map((step) => {
        if (current.matches(step)) {
          trackSignUpFunnelStep(step)
          trackAccountFlowStep({
            step: step,
            scenario: current.context.scenario,
            couponCode: pricingConfig.coupon || '',
            planDuration: current.context.plan
              ? current.context.plan.duration
              : null,
            entryPoint: current.context.entryPoint || '',
          })
        }
      })
      setLastStateValue(JSON.stringify(current.value))
    }
    // FIXME: dont ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.value])

  useEffect(() => {
    pushToDataLayer({
      event: 'signup_form',
      section: 'signup_initiated',
      label: undefined,
      success: '1', //send 1 if the signup page is loaded successfully and 0 if any error occurs
      error_message: undefined,
    })
  }, [])

  return (
    <div ref={containerRef}>
      {current.value.signUp && scenario !== 'gympass' && (
        <div className="o-width-limiter mb-24 pt-16 md:mb-40 md:pt-0">
          <ManageCoupon
            editable={false}
            hideCoupon={hideCoupon}
            pricingConfig={pricingConfig}
          />
        </div>
      )}
      {current.context.scenario === 'signup' && !current.context.plan && (
        <div className="md:pb-8 lg:pb-24">
          <ProgressIndicator current={current} />
        </div>
      )}
      <div className="c-signup-flow mx-auto flex max-w-1024 flex-col items-center">
        <MachineContext.Provider value={service}>
          <Switch current={current}>
            <Match state="choosePlan">
              <SignUpChoosePlan
                planToSelect={current.context.plan}
                onComplete={(plan) => {
                  send('NEXT', { plan })
                  scroll()
                }}
                hideCoupon={hideCoupon}
              />
            </Match>
            <Match state="decisionScreen">
              <SignUpOrLogin
                onSignUpClick={() => {
                  send('PICK_SIGNUP')
                  if (onSignUpClick) onSignUpClick()
                }}
                onLoginClick={() => {
                  send('PICK_LOGIN')
                  if (onLoginClick) onLoginClick()
                }}
              />
            </Match>
            <Match state="logIn">
              <Login
                showCloseButton={false}
                onSuccess={() => send('NEXT', { isLoggedIn: true })}
                onSignupClick={() => send('PICK_SIGNUP')}
              />
            </Match>
            <Match state="signUp.goal">
              <SignUpGoal
                onComplete={(goal) => {
                  send('NEXT', { userData: { goal } })
                  scroll()
                }}
                onLogInClick={
                  !current.context.onlySignup ? () => send('LOGIN') : undefined
                }
              />
            </Match>
            <Match state="signUp.stats">
              <SignUpStats
                userData={current.context.userData}
                onBack={hasBackButton ? () => send('PREV') : undefined}
                onChange={(userData) => send('UPDATE', { userData })}
                onComplete={() => {
                  send('NEXT')
                  scroll()
                }}
              />
            </Match>
            <Match state="signUp.pickAccountMethod">
              <SignUpPickAccountMethod
                onBack={hasBackButton ? () => send('PREV') : undefined}
                onComplete={(userData) => send('NEXT', { userData })}
              />
            </Match>
            <Match state="signUp.emailAccountCreation">
              <SignUpEmailPassword
                userData={current.context.userData}
                emailExistsError={current.context.emailExistsError}
                onBack={hasBackButton ? () => send('PREV') : undefined}
                onChange={(userData) => send('UPDATE', { userData })}
                onComplete={() => {
                  send('NEXT')
                  scroll()
                }}
              />
            </Match>
            <Match state="signUp.setUp">
              <SignUpSetUp
                onComplete={() => {
                  if (scenario === 'gympass') return navigate('/gympass')
                  send('NEXT')
                  scroll()
                }}
                onEmailExists={() => {
                  send('EMAIL_EXISTS')
                }}
                userData={current.context.userData}
              />
            </Match>
            <Match state="subscription.freePurchase">
              <SignUpFreePurchase />
            </Match>
            <Match state="subscription.choosePlan">
              <SignUpChoosePlan
                planDuration={
                  current.context.plan ? current.context.plan.duration : null
                }
                pricingConfig={pricingConfig}
                onChange={(discount) => send('UPDATE', { discount })}
                onComplete={(plan) => {
                  send('NEXT', { plan })
                  scroll()
                }}
              />
            </Match>
            <Match state="subscription.pay">
              {current.context.plan && (
                <SignUpPayment
                  onBack={hasBackButton ? () => send('PREV') : undefined}
                  onLoginClick={() => send('LOGIN')}
                  plan={current.context.plan}
                  scenario={current.context.scenario}
                  onLogInClick={() => send('LOGIN')}
                  coupon={pricingConfig.coupon}
                  pricingConfig={pricingConfig}
                  onChangePlan={() => send('CHANGE_PLAN')}
                />
              )}
            </Match>
          </Switch>
        </MachineContext.Provider>
      </div>
    </div>
  )
}

const commonProps = {
  defaultGoal: PropTypes.oneOf(goals),
  forcedGoal: PropTypes.bool,
  onlySignup: PropTypes.bool,
  onSignUpClick: PropTypes.func,
  onLoginClick: PropTypes.func,
  scenario: PropTypes.string,
  hideCoupon: PropTypes.bool,
  initialUserData: PropTypes.object,
}

AccountFlow.propTypes = {
  ...commonProps,
  initialPlan: PropTypes.object,
  discount: PropTypes.number,
  pricingConfig: PropTypes.object,
}

export default function AccountFlowWrapper({ initialPlan, ...props }) {
  const coupon = useSelector(selectCoupon)
  const pricingConfig = useSelector(selectPricingConfig(coupon))
  const regularPricingConfig = useSelector(selectRegularPricingConfig)
  const isLoading = useSelector((state) => state.user.isLoadingLimitedProfile)
  const entryPoint = getItem('EntryPoint')

  useEffect(() => {
    if (!entryPoint) setEntryPoint('Regular')
  }, [entryPoint])

  if (isLoading || !pricingConfig) return null

  let config = pricingConfig
  let plan
  if (initialPlan) {
    const findPlanByDuration = (cfg) =>
      cfg.products.find(({ duration }) => duration === initialPlan)

    if (findPlanByDuration(pricingConfig)) {
      plan = findPlanByDuration(pricingConfig)
    } else if (config.discount === 100) {
      // Free coupon, picking the first free plan
      plan = pricingConfig.products.find(({ price }) => price === 0)
    } else {
      plan = findPlanByDuration(regularPricingConfig)
      config = regularPricingConfig
    }
  }

  return (
    <AccountFlow
      {...props}
      discount={config.discount}
      initialPlan={plan}
      pricingConfig={config}
    />
  )
}

AccountFlowWrapper.propTypes = {
  ...commonProps,
  initialPlan: PropTypes.number,
}
